/**
 * Canonical Tradivo marketplace-order money model.
 *
 * Provider adapters (BigCommerce, eBay, …) translate native fields into this
 * shape. `import_marketplace_order` composes the sales-order header from it.
 *
 * Invariant: `total` is the provider's authoritative charged/order total.
 * `tax` is always retained for reporting. When `taxInclusive` is true, tax is
 * already inside line/subtotal/shipping figures and MUST NOT be added again.
 */

export type CanonicalOrderFinancials = {
  currency: string;
  subtotal: number;
  tax: number;
  shipping: number;
  discount: number;
  total: number;
  taxInclusive: boolean;
  roundingAdjustment: number;
};

export type CanonicalOrderLine = {
  sku: string | null;
  qty: number;
  unitPrice: number;
  lineTotal: number;
};

const CENTS = 2;

export function money(value: unknown, fallback = 0): number {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  if (!Number.isFinite(n)) return roundMoney(fallback);
  return roundMoney(n);
}

export function roundMoney(n: number): number {
  return Math.round((n + Number.EPSILON) * 10 ** CENTS) / 10 ** CENTS;
}

export function lineMerchandiseTotal(lines: ReadonlyArray<{ qty: number; unitPrice: number }>): number {
  return roundMoney(lines.reduce((s, l) => s + l.qty * l.unitPrice, 0));
}

/**
 * Compose Tradivo header money. `providerTotal` always wins as the charged
 * total. Composition is used to decide tax-inclusive vs exclusive when the
 * caller has not already decided, and to record residual rounding.
 */
export function composeCanonicalOrderFinancials(input: {
  subtotal: number;
  tax: number;
  shipping: number;
  discount: number;
  taxInclusive: boolean;
  providerTotal: number;
  currency?: string;
}): CanonicalOrderFinancials {
  const subtotal = money(input.subtotal);
  const tax = money(input.tax);
  const shipping = money(input.shipping);
  const discount = money(input.discount);
  const providerTotal = money(input.providerTotal);
  const composed = input.taxInclusive
    ? roundMoney(subtotal + shipping - discount)
    : roundMoney(subtotal + shipping + tax - discount);
  return {
    currency: (input.currency || "AUD").toString().trim().toUpperCase() || "AUD",
    subtotal,
    tax,
    shipping,
    discount,
    total: providerTotal,
    taxInclusive: input.taxInclusive,
    roundingAdjustment: roundMoney(providerTotal - composed),
  };
}

export function chooseTaxInclusive(opts: {
  providerTotal: number;
  exclusiveComposed: number;
  inclusiveComposed: number;
}): boolean {
  const charged = money(opts.providerTotal);
  const incDelta = Math.abs(money(opts.inclusiveComposed) - charged);
  const exDelta = Math.abs(money(opts.exclusiveComposed) - charged);
  if (incDelta === exDelta) return incDelta <= 0.02;
  return incDelta < exDelta;
}
